import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Typography, Stack, MenuItem, Select, Box } from "@mui/material";

import {
  ASTableV2,
  ASTableV2Body,
  ASTableV2Cell,
  ASTableV2Header,
  ASTableV2Row
} from "@qubit/autoparts";
import { t } from "i18next";
import { useCallback, useEffect, useState, Fragment } from "react";

import { useAppSelector, useAppDispatch } from "~/app/store";

import FetchWorkstationsControl from "~/components/FetchWorkstationsControl";
import { selectEventHistory } from "~/features/andon/andon.selectors";
import {
  selectPongs,
  selectAllPongHistories,
  selectPongHistoryTimeWindow,
  setPongHistoryTimeWindow
} from "~/features/andon/andon.slice";
import { PongData } from "~/features/andon/andon.types";
import PongControls from "~/features/demoPage/PongControls";
import {
  getMonitorTabSelectedGridId,
  setMonitorTabSelectedGridId
} from "~/features/serviceSupport/serviceSupport.slice";
import { useWorkstationPolling } from "~/hooks/useWorkstationPolling";
import { extractUserNameFromAuth0Id } from "~/lib/helpers";
import { listenAndon } from "~/lib/signalr";
import {
  selectSignalRReadyAndon,
  selectSignalRReadyMain
} from "~/redux/selectors/appSelectors";
import { selectUsersFulfillmentCenterId } from "~/redux/selectors/storeSelectors";
import { useGetAutostoreGridsQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";

import { SparkLine } from "./Sparkline";
import WorkstationsMonitorDrawer from "./WorkstationsMonitorDrawer";
import {
  calculateMemoryUsagePercentage,
  getColorForValue,
  getMemoryColor,
  getBrowserBadge,
  getOSBadge
} from "./workstationsMonitor.utils";

export type SortField =
  | "recentData"
  | "deviceId"
  | "user"
  | "eventsPerMin"
  | "lastEvent"
  | "downlink"
  | "rtt"
  | "fps"
  | "memory"
  | "loopTime"
  | "version";

const SORT_STORAGE_KEY = "ws-monitor-sort";

function loadPersistedSort(): {
  field: SortField;
  direction: "asc" | "desc";
} {
  try {
    const raw = localStorage.getItem(SORT_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as {
        field: SortField;
        direction: "asc" | "desc";
      };
      if (parsed.field && parsed.direction) return parsed;
    }
  } catch {
    // ignore
  }
  return { field: "recentData", direction: "desc" };
}

function persistSort(field: SortField, direction: "asc" | "desc"): void {
  try {
    localStorage.setItem(
      SORT_STORAGE_KEY,
      JSON.stringify({ field, direction })
    );
  } catch {
    // ignore
  }
}

export function ensureTwoPoints<T>(array: T[]): T[] {
  return array.length === 1 ? [array[0], array[0]] : array;
}

export function toggleExpandedRow(current: string[], id: string): string[] {
  return current.includes(id)
    ? current.filter((i) => i !== id)
    : [...current, id];
}

export function getActualHistoryDurationMs(
  entries: { timestamp: string }[]
): number {
  const oldest = entries.at(0)?.timestamp;
  const newest = entries.at(-1)?.timestamp;
  return oldest && newest
    ? new Date(newest).getTime() - new Date(oldest).getTime()
    : 0;
}

export const WorkstationMonitorTab = () => {
  useWorkstationPolling();
  const dispatch = useAppDispatch();

  const selectedGridId = useAppSelector(getMonitorTabSelectedGridId);
  const { data: autostoreGrids = [] } = useGetAutostoreGridsQuery();

  const pongs = useAppSelector(selectPongs);
  const pongHistories = useAppSelector(selectAllPongHistories);
  const pongHistoryTimeWindow = useAppSelector(selectPongHistoryTimeWindow);
  const eventHistory = useAppSelector(selectEventHistory);

  const usersFulfillmentCenterId = useAppSelector(
    selectUsersFulfillmentCenterId
  );
  const signalRReadyMain = useAppSelector(selectSignalRReadyMain);
  const signalRReadyAndon = useAppSelector(selectSignalRReadyAndon);

  const { data: workstations } = useGetWorkstationsQuery();

  const filteredWorkstations = (workstations ?? []).filter((ws) => {
    const matchesGrid =
      !selectedGridId ||
      selectedGridId === "" ||
      ws.autostoreGridId === selectedGridId;

    return matchesGrid;
  });

  const hasRecentData = (ws: (typeof filteredWorkstations)[number]) => {
    return Boolean(pongs?.[ws.id]?.pongData);
  };

  const persisted = loadPersistedSort();
  const [sortField, setSortFieldState] = useState<SortField>(persisted.field);
  const [sortDirection, setSortDirectionState] = useState<"asc" | "desc">(
    persisted.direction
  );

  const handleHeaderClick = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        const newDir = sortDirection === "asc" ? "desc" : "asc";
        setSortDirectionState(newDir);
        persistSort(field, newDir);
      } else {
        setSortFieldState(field);
        setSortDirectionState("asc");
        persistSort(field, "asc");
      }
    },
    [sortField, sortDirection]
  );

  // Sort workstations
  const sortedWorkstations = [...filteredWorkstations].sort((a, b) => {
    let comparison = 0;
    const aPong = pongs?.[a.id]?.pongData;
    const bPong = pongs?.[b.id]?.pongData;

    switch (sortField) {
      case "recentData": {
        const aHasData = hasRecentData(a) ? 1 : 0;
        const bHasData = hasRecentData(b) ? 1 : 0;
        comparison = bHasData - aHasData;
        break;
      }
      case "deviceId":
        comparison = (a.deviceId || "").localeCompare(b.deviceId || "");
        break;
      case "user": {
        const aUser = a.userId ? extractUserNameFromAuth0Id(a.userId) : "";
        const bUser = b.userId ? extractUserNameFromAuth0Id(b.userId) : "";
        comparison = aUser.localeCompare(bUser);
        break;
      }
      case "eventsPerMin": {
        const now = Date.now();
        const aEvents = eventHistory.filter(
          (evt) =>
            evt.workstationId === a.id &&
            now - new Date(evt.timestamp).getTime() <= pongHistoryTimeWindow
        ).length;
        const bEvents = eventHistory.filter(
          (evt) =>
            evt.workstationId === b.id &&
            now - new Date(evt.timestamp).getTime() <= pongHistoryTimeWindow
        ).length;
        comparison = aEvents - bEvents;
        break;
      }
      case "lastEvent": {
        const aLast = eventHistory
          .filter((evt) => evt.workstationId === a.id)
          .at(-1);
        const bLast = eventHistory
          .filter((evt) => evt.workstationId === b.id)
          .at(-1);
        const aTime = aLast ? new Date(aLast.timestamp).getTime() : 0;
        const bTime = bLast ? new Date(bLast.timestamp).getTime() : 0;
        comparison = aTime - bTime;
        break;
      }
      case "downlink":
        comparison =
          (aPong?.network?.downlink ?? 0) - (bPong?.network?.downlink ?? 0);
        break;
      case "rtt":
        comparison = (aPong?.network?.rtt ?? 0) - (bPong?.network?.rtt ?? 0);
        break;
      case "fps":
        comparison = (aPong?.fps ?? 0) - (bPong?.fps ?? 0);
        break;
      case "memory": {
        const aMemPct =
          aPong?.memory?.usedJSHeapSize && aPong?.memory?.jsHeapSizeLimit
            ? aPong.memory.usedJSHeapSize / aPong.memory.jsHeapSizeLimit
            : 0;
        const bMemPct =
          bPong?.memory?.usedJSHeapSize && bPong?.memory?.jsHeapSizeLimit
            ? bPong.memory.usedJSHeapSize / bPong.memory.jsHeapSizeLimit
            : 0;
        comparison = aMemPct - bMemPct;
        break;
      }
      case "loopTime":
        comparison =
          (pongs?.[a.id]?.pongLoopTime ?? 0) -
          (pongs?.[b.id]?.pongLoopTime ?? 0);
        break;
      case "version":
        comparison = (aPong?.clientVersion ?? "").localeCompare(
          bPong?.clientVersion ?? ""
        );
        break;
    }

    return sortDirection === "asc" ? comparison : -comparison;
  });

  const [drawerRowId, setDrawerRowId] = useState<string | null>(null);

  const pongHistoryOptions = [
    { label: "10 seconds", value: 10 * 1000 },
    { label: "30 seconds", value: 30 * 1000 },
    { label: "1 minute", value: 1 * 60 * 1000 },
    { label: "5 minutes", value: 5 * 60 * 1000 },
    { label: "10 minutes", value: 10 * 60 * 1000 },
    { label: "30 minutes", value: 30 * 60 * 1000 },
    { label: "1 hour", value: 60 * 60 * 1000 },
    { label: "4 hours", value: 4 * 60 * 60 * 1000 },
    { label: "24 hours", value: 24 * 60 * 60 * 1000 },
    { label: "All", value: Number.MAX_SAFE_INTEGER }
  ];

  useEffect(() => {
    if (signalRReadyMain && usersFulfillmentCenterId && !signalRReadyAndon) {
      // to listen for pongs on the heartbeat hub
      void listenAndon({ usersFulfillmentCenterId, dispatch });
    }
  }, [signalRReadyMain, usersFulfillmentCenterId, signalRReadyAndon, dispatch]);

  const allHistoryEntries = Object.values(pongHistories).flat();

  const actualHistoryDurationMs = getActualHistoryDurationMs(allHistoryEntries);

  const selectedWorkstation = sortedWorkstations.find(
    (ws) => ws.id === drawerRowId
  );

  return (
    <Stack spacing={2} height="100%" width="100%">
      {/* Header controls */}
      <Stack
        direction="row"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        sx={{ mt: 2 }}
      >
        {/* Workstation data controls */}
        <Stack direction="row" alignItems="center" gap={1}>
          <Select
            size="small"
            value={selectedGridId ?? ""}
            onChange={(e) =>
              dispatch(setMonitorTabSelectedGridId(e.target.value))
            }
            renderValue={(selected) =>
              selected
                ? autostoreGrids.find((g) => g.autostoreGridId === selected)
                    ?.autostoreGridName
                : t("all grids")
            }
            displayEmpty
            inputProps={{ "aria-label": t("autostore grid") }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">
              <em>{t("all grids")}</em>
            </MenuItem>
            {autostoreGrids.map((grid) => (
              <MenuItem key={grid.autostoreGridId} value={grid.autostoreGridId}>
                {grid.autostoreGridName}
              </MenuItem>
            ))}
          </Select>
          <FetchWorkstationsControl />
        </Stack>

        {/* Ping diagnostics controls */}
        <PongControls
          additionalControls={
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" color="text.secondary" noWrap>
                {t("time filter")}
              </Typography>
              <Select
                size="small"
                value={pongHistoryTimeWindow}
                disabled={actualHistoryDurationMs === 0}
                onChange={(e) =>
                  dispatch(setPongHistoryTimeWindow(Number(e.target.value)))
                }
                sx={{ width: 160 }}
              >
                {pongHistoryOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    disabled={
                      actualHistoryDurationMs < option.value && option.value > 0
                    }
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          }
        />
      </Stack>

      {/* workstation data table */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          pb: 2
        }}
      >
        <ASTableV2
          containerSx={{ maxHeight: "80vh" }}
          gridTemplateColumns={`minmax(200px, 2fr) repeat(8, minmax(120px, 1fr))`}
          isLoading={false}
          stickyHeader
        >
          <ASTableV2Header>
            <ASTableV2Row>
              {(
                [
                  { field: "deviceId", label: t("workstation") },
                  { field: "eventsPerMin", label: t("events/min") },
                  { field: "lastEvent", label: t("last event") },
                  { field: "downlink", label: t("downlink") },
                  { field: "rtt", label: t("rtt") },
                  { field: "fps", label: t("fps") },
                  { field: "memory", label: t("memory") },
                  { field: "loopTime", label: t("ping loop time") },
                  { field: "version", label: "Version" }
                ] as { field: SortField; label: string }[]
              ).map(({ field, label }) => (
                <ASTableV2Cell
                  key={field}
                  sx={{
                    justifyContent:
                      field === "deviceId" ? "flex-start" : "center",
                    cursor: "pointer",
                    userSelect: "none",
                    "&:hover": { bgcolor: "action.hover" }
                  }}
                  onClick={() => handleHeaderClick(field)}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    <span>{label}</span>
                    {sortField === field &&
                      (sortDirection === "asc" ? (
                        <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                      ) : (
                        <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                      ))}
                  </Stack>
                </ASTableV2Cell>
              ))}
            </ASTableV2Row>
          </ASTableV2Header>

          <ASTableV2Body>
            {sortedWorkstations.map((ws) => {
              const { userId } = ws;

              const now = Date.now();
              const pong = pongs?.[ws.id];
              const pongData = pong?.pongData;

              const loopTime = pong?.pongLoopTime;

              const downlink = pongData?.network?.downlink;
              const rtt = pongData?.network?.rtt;

              const pongHistory = pongHistories[ws.id] ?? [];

              const history = pongHistory.filter(
                (entry) =>
                  now - new Date(entry.timestamp).getTime() <=
                  pongHistoryTimeWindow
              );

              // Calculate events per minute and time since last event
              const workstationEvents = eventHistory.filter(
                (evt) => evt.workstationId === ws.id
              );

              const recentEvents = workstationEvents.filter(
                (evt) =>
                  now - new Date(evt.timestamp).getTime() <=
                  pongHistoryTimeWindow
              );

              const eventsPerMin =
                recentEvents.length > 0
                  ? (
                      recentEvents.length /
                      (pongHistoryTimeWindow / 60000)
                    ).toFixed(1)
                  : "0.0";

              const lastEvent =
                workstationEvents.length > 0
                  ? workstationEvents[workstationEvents.length - 1]
                  : null;

              const timeSinceLastEvent = lastEvent
                ? now - new Date(lastEvent.timestamp).getTime()
                : null;

              const formatTimeSince = (ms: number | null): string => {
                if (ms === null) return "-";
                const seconds = Math.floor(ms / 1000);
                if (seconds < 60) return `${seconds}s`;
                const minutes = Math.floor(seconds / 60);
                if (minutes < 60) return `${minutes}m`;
                const hours = Math.floor(minutes / 60);
                return `${hours}h`;
              };

              const buildMetricCell = ({
                current,
                historyEntries,
                accessor,
                unit,
                colorFunc
              }: {
                current: number | string | undefined;
                historyEntries: {
                  timestamp: string;
                  pongData: PongData;
                  loopTime: number;
                }[];
                accessor: (entry: (typeof historyEntries)[number]) => number;
                unit: string;
                defaultMax: number;
                colorFunc?: (value: number) => string | undefined;
              }) => {
                const data = historyEntries.map(accessor);
                const timestamps = historyEntries.map(
                  (entry) => entry.timestamp
                );

                return (
                  <Stack direction="column" alignItems="center" spacing={0.5}>
                    {/* Sparkline Chart */}
                    {current !== undefined && (
                      <SparkLine
                        data={ensureTwoPoints(data)}
                        timestamps={ensureTwoPoints(timestamps)}
                        yMin={0}
                      />
                    )}

                    {/* Current Value */}
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        textAlign: "center",
                        color: colorFunc
                          ? colorFunc(Number(current) || 0)
                          : undefined
                      }}
                    >
                      {current !== undefined ? `${current} ${unit}` : " "}
                    </Typography>
                  </Stack>
                );
              };
              const browserBadge = getBrowserBadge(
                pongData?.userAgentParsed?.browser
              );
              const osBadge = getOSBadge(pongData?.userAgentParsed?.os);

              const isRowDisabled = !hasRecentData(ws);

              return (
                <Fragment key={ws.id}>
                  <ASTableV2Row
                    selected={drawerRowId === ws.id}
                    onClick={
                      isRowDisabled ? undefined : () => setDrawerRowId(ws.id)
                    }
                    sx={{
                      opacity: isRowDisabled ? 0.5 : 1,
                      cursor: isRowDisabled ? "default" : "pointer"
                    }}
                  >
                    {/* Workstation */}
                    <ASTableV2Cell sx={{ minWidth: 200 }}>
                      <Stack
                        direction="column"
                        spacing={0.75}
                        alignItems="flex-start"
                      >
                        <Typography
                          variant="tableBody"
                          sx={{ minWidth: 180, minHeight: 24 }}
                        >
                          {ws.deviceId || "Placeholder-ID"}
                        </Typography>

                        <Stack direction="row" spacing={0.5} height={24}>
                          {!isRowDisabled ? (
                            <>
                              <Box
                                sx={{
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  border: `1px solid ${browserBadge.border}`,
                                  bgcolor: browserBadge.color,
                                  fontSize: 12,
                                  fontWeight: 500,
                                  color: "text.primary"
                                }}
                              >
                                {browserBadge.label}
                              </Box>
                              <Box
                                sx={{
                                  px: 1,
                                  py: 0.25,
                                  borderRadius: 1,
                                  border: `1px solid ${osBadge.border}`,
                                  bgcolor: osBadge.color,
                                  fontSize: 12,
                                  fontWeight: 500,
                                  color: "text.primary"
                                }}
                              >
                                {osBadge.label}
                              </Box>
                            </>
                          ) : (
                            <></>
                          )}
                        </Stack>
                        <Typography variant="tableBody">
                          {!isRowDisabled && userId
                            ? extractUserNameFromAuth0Id(userId)
                            : ""}
                        </Typography>
                        {!isRowDisabled && (
                          <Stack direction="row" spacing={0.5}>
                            {ws.mode && ws.mode !== "Undefined" && (
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary" }}
                              >
                                {ws.mode}
                              </Typography>
                            )}
                            {ws.status && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color:
                                    ws.status === "Active"
                                      ? "success.main"
                                      : ws.status === "Faulted"
                                        ? "error.main"
                                        : "text.secondary"
                                }}
                              >
                                ({ws.status})
                              </Typography>
                            )}
                          </Stack>
                        )}
                      </Stack>
                    </ASTableV2Cell>

                    {/* Events Per Minute */}
                    <ASTableV2Cell sx={{ justifyContent: "center" }}>
                      <Typography variant="body2" textAlign="center">
                        {eventsPerMin}
                      </Typography>
                    </ASTableV2Cell>

                    {/* Time Since Last Event */}
                    <ASTableV2Cell sx={{ justifyContent: "center" }}>
                      <Typography variant="body2" textAlign="center">
                        {formatTimeSince(timeSinceLastEvent)}
                      </Typography>
                    </ASTableV2Cell>

                    {/* Downlink */}
                    <ASTableV2Cell sx={{ justifyContent: "center" }}>
                      {downlink
                        ? buildMetricCell({
                            current: downlink,
                            historyEntries: history,
                            accessor: (entry) =>
                              entry.pongData?.network?.downlink ?? 0,
                            unit: "Mbps",
                            defaultMax: 20,
                            colorFunc: (val) =>
                              getColorForValue(val, "downlink")
                          })
                        : null}
                    </ASTableV2Cell>

                    {/* RTT */}
                    <ASTableV2Cell sx={{ justifyContent: "center" }}>
                      {rtt
                        ? buildMetricCell({
                            current: rtt,
                            historyEntries: history,
                            accessor: (entry) =>
                              entry.pongData?.network?.rtt ?? 0,
                            unit: "ms",
                            defaultMax: 500
                          })
                        : null}
                    </ASTableV2Cell>

                    {/* FPS */}
                    <ASTableV2Cell sx={{ justifyContent: "center" }}>
                      {pongData?.fps
                        ? buildMetricCell({
                            current: pongData.fps,
                            historyEntries: history,
                            accessor: (entry) => entry.pongData.fps ?? 0,
                            unit: "fps",
                            defaultMax: 60,
                            colorFunc: (val) => getColorForValue(val, "fps")
                          })
                        : null}
                    </ASTableV2Cell>

                    {/* Memory */}
                    <ASTableV2Cell sx={{ justifyContent: "center" }}>
                      {buildMetricCell({
                        current: pongData?.memory
                          ? `${calculateMemoryUsagePercentage(pongData.memory)}`
                          : undefined,
                        historyEntries: history,
                        accessor: (entry) => {
                          const mem = entry.pongData.memory;
                          if (mem?.usedJSHeapSize && mem?.jsHeapSizeLimit) {
                            return (
                              Math.round(
                                (mem.usedJSHeapSize / mem.jsHeapSizeLimit) *
                                  10000
                              ) / 100
                            );
                          }
                          return 0;
                        },
                        unit: "%",
                        defaultMax: 100,
                        colorFunc: () => getMemoryColor(pongData?.memory)
                      })}
                    </ASTableV2Cell>

                    {/* Loop Time */}
                    <ASTableV2Cell sx={{ justifyContent: "center" }}>
                      {buildMetricCell({
                        current: loopTime,
                        historyEntries: history,
                        accessor: (entry) => entry.loopTime ?? 0,
                        unit: "ms",
                        defaultMax: 500
                      })}
                    </ASTableV2Cell>

                    {/* Version */}
                    <ASTableV2Cell sx={{ justifyContent: "center" }}>
                      <Stack
                        direction="column"
                        alignItems="center"
                        spacing={0.5}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {pongData?.clientVersion || "-"}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                        >
                          {pongData?.serverVersion?.tag ||
                            pongData?.serverVersion?.hash ||
                            "-"}
                        </Typography>
                      </Stack>
                    </ASTableV2Cell>
                  </ASTableV2Row>
                </Fragment>
              );
            })}
          </ASTableV2Body>
        </ASTableV2>

        <WorkstationsMonitorDrawer
          open={!!drawerRowId}
          workstation={selectedWorkstation ?? null}
          onClose={() => setDrawerRowId(null)}
        />
      </Box>
    </Stack>
  );
};

export default WorkstationMonitorTab;
